import { useState, useCallback, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom"
import {
  Box,
  Container,
  Typography,
  Paper,
  Collapse,
  Chip,
  Stack,
  Button,
  TextField,
  InputAdornment,
  Alert,
} from "@mui/material";
import {
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import { useGetJobAsUser, type JobResponse } from "@taxassist/api-client";
import { AdvancedFilters } from "../../components/list/AdvancedFilters";
import { JobsDataTable } from "../../components/list/JobsDataTable";
import { LoadingSpinner } from "../../components/common/LoadingSpinner";
import { CreateJobDialog } from "../../components/jobs/CreateJobDialog";
import { JobDetailsDrawer } from "../../components/jobs/JobDetailsDrawer";
import {
  NotificationSnackbar,
  useNotification,
} from "../../components/common/NotificationSnackbar";
import { useFirm, useCurrentFirmId } from "../../contexts/FirmContext";
import { useFilteredJobs, useJobs } from "../../hooks/useJobs";
import { useAuth } from "../../contexts/AuthContext";
import { UserRole } from "@taxassist/api-client";
import { useJobFilters, type JobFilters } from "../../hooks/useJobFilters";
import type { UserFriendlyStatus } from "../../utils/statusMapping";

// Re-export for components that need it
export type { JobFilters };
import { useSortedAndPaginatedJobs } from "../../hooks/useSortedAndPaginatedJobs";

export interface SortConfig {
  field: keyof JobResponse;
  direction: "asc" | "desc";
}

export function ListJobsView() {
  const theme = useTheme();
  const { jobId: jobIdParam } = useParams();
  const navigate = useNavigate();

  // Use 'temporary' for small screens (e.g., mobile/tablet)
  // Use 'panel' (or 'permanent') for large screens
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const drawerVariant: "temporary" | "panel" = isSmallScreen ? "temporary" : "panel";

  const { user } = useAuth();
  const {
    availableFirms,
    isLoading: firmLoading,
    error: firmError,
  } = useFirm();
  const currentFirmId = useCurrentFirmId();

  // UI state
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [createJobDialogOpen, setCreateJobDialogOpen] = useState(false);
  const [selectedJobForDetails, setSelectedJobForDetails] = useState<
    string | null
  >(null);
  const [selectedFirmIdForDetails, setSelectedFirmIdForDetails] = useState<
    string | null
  >(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const { successMessage, showSuccess, clearSuccess } = useNotification();

  // Filter management
  const {
    filters,
    searchQuery,
    activeFiltersCount,
    setSearchQuery,
    setFilters: handleFiltersChange,
    clearFilter,
    clearAllFilters,
  } = useJobFilters();

  // Data fetching with filters
  const {
    jobs: filteredJobs,
    isLoading,
    error,
    refetch,
  } = useFilteredJobs(filters);

  // Count unsubmitted (DRAFT) jobs from unfiltered data
  const { data: allJobs = [] } = useJobs();
  const draftJobCount = useMemo(
    () => allJobs.filter((job) => job.status === "DRAFT").length,
    [allJobs],
  );

  const isFilteredToUnsubmitted = filters.userFriendlyStatuses?.includes(
    "Unsubmitted" as UserFriendlyStatus,
  );

  const handleShowUnsubmitted = useCallback(() => {
    handleFiltersChange({
      ...filters,
      userFriendlyStatuses: ["Unsubmitted" as UserFriendlyStatus],
    });
  }, [filters, handleFiltersChange]);

  useEffect(() => {
    if (jobIdParam) {
      setSelectedJobForDetails(jobIdParam);
      setDetailsOpen(true);
    } else {
      setDetailsOpen(false);
      setSelectedJobForDetails(null);
    }
  }, [jobIdParam]);

  // Sorting and pagination
  const {
    sortConfig,
    page,
    pageSize,
    handleSortChange,
    handlePageChange,
    handlePageSizeChange,
    resetPagination,
    sortedJobs: filteredAndSortedJobs,
  } = useSortedAndPaginatedJobs({
    jobs: filteredJobs,
    initialSort: { field: "updatedAt", direction: "desc" },
  });

  // Event handlers
  const handleFiltersChangeWithReset = useCallback(
    (newFilters: JobFilters) => {
      handleFiltersChange(newFilters);
      resetPagination(); // Reset to first page when filters change
    },
    [handleFiltersChange, resetPagination],
  );

  const handleJobCreated = useCallback(
    (jobId: string) => {
      showSuccess(`Job ${jobId} created successfully!`);
      setCreateJobDialogOpen(false);
      setSelectedJobForDetails(jobId);
      setDetailsOpen(true);
      refetch();
    },
    [refetch, showSuccess],
  );

  const handleJobSelected = useCallback((jobId: string, firmId: string) => {
    setSelectedJobForDetails(jobId);
    setSelectedFirmIdForDetails(firmId);
    setDetailsOpen(true);
    navigate(`/jobs/${jobId}`, { replace: true, state: { firmKnown: true } });
  }, [navigate]);

  const handleDetailsClose = useCallback(() => {
    setDetailsOpen(false);
    setSelectedJobForDetails(null);
    navigate('/jobs', { replace: true });
  }, [navigate]);

  // Helper function to determine what to show when no firm is selected
  const getNoFirmSelectedContent = () => {
    const isAdmin =
      user?.role === UserRole.admin || user?.role === UserRole.reviewer;

    if (isAdmin && availableFirms.length > 0) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "400px",
            gap: 2,
          }}
        >
          <Typography variant="h6" color="text.primary">
            Select a Firm to View Jobs
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Use the firm selector in the header to choose which firm's jobs you
            want to view.
          </Typography>
        </Box>
      );
    } else if (isAdmin && availableFirms.length === 0 && !firmLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "400px",
            gap: 2,
          }}
        >
          <Typography variant="h6" color="text.primary">
            No Firms Available
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            There are no firms accessible to your account at this time.
          </Typography>
        </Box>
      );
    }

    // Show error if firm context failed (e.g., no firms assigned)
    if (firmError && !firmLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "400px",
            gap: 2,
          }}
        >
          <Typography variant="h6" color="text.primary">
            Unable to Load Jobs
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
          >
            {firmError}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
          >
            Please contact your administrator to ensure your account is
            properly configured.
          </Typography>
        </Box>
      );
    }

    // Still loading firm context
    return <LoadingSpinner message="Loading jobs..." />;
  };

  if (error) {
    return (
      <Container>
        <Box py={4}>
          <Alert severity="error">
            Failed to load jobs. Please try again later.
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Box
        sx={{
          bgcolor: "background.paper",
          height: "calc(100vh - 65px)",
          display: "flex",
          overflow: "hidden",
        }}
      >
        {/* Main content area that shrinks when details panel is open */}
        <Box
          sx={{
            flex: "1 1 auto",
            transition: "flex 0.3s ease-in-out",
            overflow: "hidden",
            maxWidth: "calc(100vw - 84px)",
            [theme.breakpoints.down("sm")]: {
              maxWidth: "calc(100vw - 12px)",
            },
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              pb: 2,
            }}
          >
            {/* Header */}
            <Box sx={{ py: 3, borderBottom: 1, borderColor: "divider" }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <Stack direction="row" alignItems="center" gap={2}>
                  <Typography
                    variant="h4"
                    component="h1"
                    sx={{ fontFamily: "Media77", fontWeight: 400 }}
                  >
                    TaxAssist
                  </Typography>
                  <Chip
                    label={`${filteredAndSortedJobs.length}`}
                    size="small"
                    sx={{ color: "#757575", fontWeight: "500" }}
                  />
                </Stack>
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateJobDialogOpen(true)}
                  sx={{
                    fontWeight: 600,
                    backgroundColor: "#082042",
                    color: "#FFFFFF",
                    "&:hover": {
                      backgroundColor: "#0A2A5A",
                    },
                  }}
                >
                  CREATE JOB
                </Button>
              </Box>

              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <TextField
                  placeholder="Search by client name, ID"
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ minWidth: 250 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                <Box display="flex" alignItems="center" gap={2}>
                  <Button
                    variant={filtersOpen ? "contained" : "outlined"}
                    size="medium"
                    startIcon={<FilterListIcon />}
                    endIcon={
                      <ExpandMoreIcon
                        sx={{
                          transform: filtersOpen
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                          transition: "transform 0.2s",
                        }}
                      />
                    }
                    onClick={() => setFiltersOpen(!filtersOpen)}
                    sx={{ minWidth: 100 }}
                  >
                    Filters
                  </Button>
                </Box>
              </Box>

              {/* Active Filters */}
              {activeFiltersCount > 0 && (
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  gap={1}
                  mb={2}
                >
                  {filters.search && (
                    <Chip
                      label={`Search: "${filters.search}"`}
                      size="small"
                      onDelete={() => clearFilter("search")}
                      variant="outlined"
                    />
                  )}
                  {filters.userFriendlyStatuses &&
                    filters.userFriendlyStatuses.length > 0 && (
                      <Chip
                        label={`Status: ${filters.userFriendlyStatuses.length} selected`}
                        size="small"
                        onDelete={() => clearFilter("userFriendlyStatuses")}
                        variant="outlined"
                      />
                    )}
                  {filters.filingTypes && filters.filingTypes.length > 0 && (
                    <Chip
                      label={`Filing Type: ${filters.filingTypes.length} selected`}
                      size="small"
                      onDelete={() => clearFilter("filingTypes")}
                      variant="outlined"
                    />
                  )}
                  {(filters.dateRange?.start || filters.dateRange?.end) && (
                    <Chip
                      label="Date Range"
                      size="small"
                      onDelete={() => clearFilter("dateRange")}
                      variant="outlined"
                    />
                  )}
                </Stack>
              )}

              {/* Collapsible Filters Section */}
              <Collapse in={filtersOpen} timeout="auto" unmountOnExit>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 2,
                    borderRadius: 2,
                    bgcolor: "#FFFFFF",
                    border: 1,
                    borderColor: "divider",
                  }}
                >
                  <AdvancedFilters
                    filters={filters}
                    onFiltersChange={handleFiltersChangeWithReset}
                    onClearAll={clearAllFilters}
                    jobsCount={filteredAndSortedJobs.length}
                  />
                </Paper>
              </Collapse>
            </Box>

            {/* Unsubmitted jobs banner */}
            {draftJobCount > 0 && !isFilteredToUnsubmitted && (
              <Alert
                severity="info"
                sx={{ mb: 1 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleShowUnsubmitted}
                  >
                    View
                  </Button>
                }
              >
                You have {draftJobCount} unsubmitted{" "}
                {draftJobCount === 1 ? "job" : "jobs"} waiting to be submitted.
              </Alert>
            )}

            {/* Data Table */}
            <Paper
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              {!currentFirmId ? (
                getNoFirmSelectedContent()
              ) : isLoading && !filteredJobs.length ? (
                <LoadingSpinner message="Loading jobs..." />
              ) : (
                <JobsDataTable
                    jobs={filteredAndSortedJobs}
                    isLoading={isLoading}
                    sortConfig={sortConfig}
                    onSortChange={handleSortChange}
                    selectedJobs={new Set()}
                    onSelectionChange={() => {}}
                    page={page}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    onJobSelected={handleJobSelected}
                    selectedJobId={selectedJobForDetails || undefined}
                  />
              )}
            </Paper>
          </Container>
        </Box>

        {/* Right Panel for Job Details */}
        {drawerVariant === "temporary" ? (
          <JobDetailsDrawer
            open={detailsOpen}
            onClose={handleDetailsClose}
            jobId={selectedJobForDetails!}
            firmId={selectedFirmIdForDetails || undefined}
            variant={drawerVariant}
          />
        ) : (
          <Box
            sx={{
              flex: detailsOpen ? "0 0 40%" : "0 0 0%",
              height: "100%",
              overflow: "auto",
              borderLeft: 1,
              borderColor: "divider",
              backgroundColor: "#FFFFFF",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <JobDetailsDrawer
              open={detailsOpen}
              onClose={handleDetailsClose}
              jobId={selectedJobForDetails!}
              firmId={selectedFirmIdForDetails || undefined}
              variant={drawerVariant}
            />
          </Box>
        )}
      </Box>

      {/* Success Notification */}
      <NotificationSnackbar
        message={successMessage}
        onClose={clearSuccess}
        severity="success"
      />

      {/* Create Job Dialog */}
      {currentFirmId && (
        <CreateJobDialog
          open={createJobDialogOpen}
          onClose={() => setCreateJobDialogOpen(false)}
          firmId={currentFirmId}
          onJobCreated={handleJobCreated}
        />
      )}
    </>
  );
}
